/**
 * Hold & Spin Bonus Game Component
 *
 * A Lightning Link-style bonus game where:
 * - All 15 positions spin simultaneously
 * - Gold rings with values "hold" in place
 * - 3 spins, reset when new ring lands
 * - Fill all 15 for GRAND BONUS!
 */
/* eslint-disable rumio-i18n/no-hardcoded-strings */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================================================
// Types
// =============================================================================

interface GridPosition {
  hasRing: boolean;
  value: number | null;
}

interface HoldAndSpinBonusProps {
  /** Whether the bonus game is active */
  isActive: boolean;
  /** Initial grid state (from API) */
  initialGrid?: GridPosition[];
  /** Initial spins remaining */
  initialSpins?: number;
  /** Bet per line (for display) */
  betPerLine?: number;
  /** Function to execute a spin round */
  onSpin: () => Promise<{
    grid: GridPosition[];
    spinsRemaining: number;
    totalValue: number;
    newRings: Array<{ position: number; value: number }>;
    isComplete: boolean;
    isGrandBonus: boolean;
    payout?: number;
  } | null>;
  /** Called when bonus game is complete */
  onComplete: (payout: number, isGrandBonus: boolean) => void;
  /** Called to close the bonus game overlay */
  onClose: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function HoldAndSpinBonus({
  isActive,
  initialGrid,
  initialSpins = 3,
  onSpin,
  onComplete,
  onClose,
}: HoldAndSpinBonusProps) {
  // Game state
  const [grid, setGrid] = useState<GridPosition[]>(
    initialGrid || Array(15).fill({ hasRing: false, value: null })
  );
  const [spinsRemaining, setSpinsRemaining] = useState(initialSpins);
  const [totalValue, setTotalValue] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [newRingPositions, setNewRingPositions] = useState<number[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isGrandBonus, setIsGrandBonus] = useState(false);
  const [finalPayout, setFinalPayout] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  // Auto-spin on mount and after each spin
  const autoSpinRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculate rings landed
  const ringsLanded = grid.filter((p) => p.hasRing).length;

  // Handle spin
  const handleSpin = useCallback(async () => {
    if (isSpinning || isComplete) return;

    setIsSpinning(true);
    setNewRingPositions([]);

    // Simulate spin animation delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      const result = await onSpin();
      if (!result) {
        setIsSpinning(false);
        return;
      }

      // Update state with results
      setGrid(result.grid);
      setSpinsRemaining(result.spinsRemaining);
      setTotalValue(result.totalValue);
      setNewRingPositions(result.newRings.map((r) => r.position));

      if (result.isComplete) {
        setIsComplete(true);
        setIsGrandBonus(result.isGrandBonus);
        setFinalPayout(result.payout || result.totalValue);

        // Show celebration
        setTimeout(() => {
          setShowCelebration(true);
        }, 500);
      }
    } catch (error) {
      console.error('Hold & Spin error:', error);
    }

    setIsSpinning(false);
  }, [isSpinning, isComplete, onSpin]);

  // Auto-spin effect
  useEffect(() => {
    if (isActive && !isComplete && !isSpinning && spinsRemaining > 0) {
      autoSpinRef.current = setTimeout(() => {
        handleSpin();
      }, 1000);
    }

    return () => {
      if (autoSpinRef.current) {
        clearTimeout(autoSpinRef.current);
      }
    };
  }, [isActive, isComplete, isSpinning, spinsRemaining, handleSpin]);

  // Handle celebration complete
  const handleCelebrationComplete = useCallback(() => {
    onComplete(finalPayout, isGrandBonus);
    onClose();
  }, [finalPayout, isGrandBonus, onComplete, onClose]);

  if (!isActive) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-2xl mx-4">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <h2 className="text-4xl font-bold text-yellow-400 drop-shadow-lg">
            HOLD & SPIN
          </h2>
          <p className="text-lg text-white/80 mt-2">
            Land rings to win! Fill all 15 for GRAND BONUS!
          </p>
        </motion.div>

        {/* Stats Bar */}
        <div className="flex justify-between items-center mb-4 px-4">
          <div className="text-center">
            <div className="text-sm text-white/60">Spins</div>
            <motion.div
              key={spinsRemaining}
              initial={{ scale: 1.5 }}
              animate={{ scale: 1 }}
              className="text-3xl font-bold text-yellow-400"
            >
              {spinsRemaining}
            </motion.div>
          </div>

          <div className="text-center">
            <div className="text-sm text-white/60">Rings</div>
            <div className="text-2xl font-bold text-white">
              {ringsLanded}/15
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-white/60">Total</div>
            <motion.div
              key={totalValue}
              initial={{ scale: 1.3 }}
              animate={{ scale: 1 }}
              className="text-3xl font-bold text-green-400"
            >
              {totalValue}
            </motion.div>
          </div>
        </div>

        {/* Game Grid (3 rows x 5 columns) */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 border border-yellow-500/30"
        >
          <div className="grid grid-cols-5 gap-3">
            {grid.map((position, index) => (
              <GridCell
                key={index}
                position={position}
                index={index}
                isNew={newRingPositions.includes(index)}
                isSpinning={isSpinning && !position.hasRing}
              />
            ))}
          </div>
        </motion.div>

        {/* Spin Button */}
        {!isComplete && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSpin}
            disabled={isSpinning || spinsRemaining <= 0}
            className={`
              w-full mt-6 py-4 rounded-xl font-bold text-xl
              transition-all duration-200
              ${
                isSpinning || spinsRemaining <= 0
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-400 hover:to-orange-400'
              }
            `}
          >
            {isSpinning ? (
              <span className="flex items-center justify-center gap-2">
                <SpinnerIcon /> SPINNING...
              </span>
            ) : spinsRemaining <= 0 ? (
              'OUT OF SPINS'
            ) : (
              'SPIN'
            )}
          </motion.button>
        )}

        {/* Celebration Overlay */}
        <AnimatePresence>
          {showCelebration && (
            <CelebrationOverlay
              isGrandBonus={isGrandBonus}
              payout={finalPayout}
              onComplete={handleCelebrationComplete}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Grid Cell Component
// =============================================================================

interface GridCellProps {
  position: GridPosition;
  index: number;
  isNew: boolean;
  isSpinning: boolean;
}

function GridCell({ position, isNew, isSpinning }: GridCellProps) {
  return (
    <motion.div
      initial={isNew ? { scale: 0, rotate: 180 } : false}
      animate={
        isNew
          ? { scale: 1, rotate: 0 }
          : isSpinning
          ? { opacity: [0.5, 1, 0.5] }
          : { scale: 1 }
      }
      transition={
        isNew
          ? { type: 'spring', stiffness: 300, damping: 15 }
          : isSpinning
          ? { duration: 0.3, repeat: Infinity }
          : {}
      }
      className={`
        aspect-square rounded-xl flex items-center justify-center
        border-2 transition-all duration-300
        ${
          position.hasRing
            ? 'bg-gradient-to-br from-yellow-500 to-amber-600 border-yellow-400 shadow-lg shadow-yellow-500/30'
            : 'bg-slate-800/50 border-slate-700'
        }
        ${isNew ? 'ring-4 ring-yellow-400 ring-opacity-50' : ''}
      `}
    >
      {position.hasRing ? (
        <motion.div
          initial={isNew ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Ring icon */}
          <div className="text-2xl mb-1">💍</div>
          {/* Value */}
          <div className="text-lg font-bold text-white drop-shadow-md">
            {position.value}
          </div>
        </motion.div>
      ) : isSpinning ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 0.5, repeat: Infinity, ease: 'linear' }}
          className="text-2xl opacity-50"
        >
          ✨
        </motion.div>
      ) : (
        <div className="text-3xl text-slate-600 opacity-30">?</div>
      )}
    </motion.div>
  );
}

// =============================================================================
// Celebration Overlay
// =============================================================================

interface CelebrationOverlayProps {
  isGrandBonus: boolean;
  payout: number;
  onComplete: () => void;
}

function CelebrationOverlay({
  isGrandBonus,
  payout,
  onComplete,
}: CelebrationOverlayProps) {
  // Auto-close after delay
  useEffect(() => {
    const timer = setTimeout(onComplete, isGrandBonus ? 5000 : 3000);
    return () => clearTimeout(timer);
  }, [isGrandBonus, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.2, 1] }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        {isGrandBonus ? (
          <>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="text-6xl mb-4"
            >
              🎰
            </motion.div>
            <h3 className="text-5xl font-bold text-yellow-400 mb-2 drop-shadow-lg">
              GRAND BONUS!
            </h3>
            <p className="text-xl text-white/80 mb-4">ALL 15 RINGS!</p>
          </>
        ) : (
          <>
            <div className="text-5xl mb-4">🎉</div>
            <h3 className="text-4xl font-bold text-green-400 mb-2">
              BONUS COMPLETE!
            </h3>
          </>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-white"
        >
          +{payout} Rumios
        </motion.div>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          onClick={onComplete}
          className="mt-6 px-8 py-3 bg-yellow-500 text-white rounded-lg font-bold hover:bg-yellow-400 transition-colors"
        >
          COLLECT
        </motion.button>
      </motion.div>

      {/* Confetti effect for Grand Bonus */}
      {isGrandBonus && <ConfettiEffect />}
    </motion.div>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

function SpinnerIcon() {
  return (
    <svg
      className="animate-spin h-6 w-6"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ConfettiEffect() {
  // Simple confetti particles
  const particles = Array.from({ length: 50 });
  const colors = ['#FFD700', '#FF6B35', '#10B981', '#3B82F6', '#EC4899'];

  // Pre-generate random values for all particles once on mount
  const [particleData] = useState(() => {
    const generateRandom = () => Math.random();
    return particles.map(() => ({
      xTarget: generateRandom() * 100,
      yTarget: generateRandom() * 100,
      rotate: generateRandom() * 720,
      delay: generateRandom() * 0.5,
      color: colors[Math.floor(generateRandom() * colors.length)],
    }));
  });

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const data = particleData[i];
        return (
          <motion.div
            key={i}
            initial={{
              opacity: 1,
              x: '50%',
              y: '50%',
              scale: 0,
            }}
            animate={{
              opacity: [1, 1, 0],
              x: `${data.xTarget}%`,
              y: `${data.yTarget}%`,
              scale: [0, 1, 0.5],
              rotate: data.rotate,
            }}
            transition={{
              duration: 2,
              delay: data.delay,
              ease: 'easeOut',
            }}
            className="absolute w-3 h-3 rounded-full"
            style={{
              backgroundColor: data.color,
            }}
          />
        );
      })}
    </div>
  );
}

export default HoldAndSpinBonus;
